'use client';

/**
 * Pre-call microphone diagnostic page.
 *
 * Volume meter primary (works in iOS Safari + every modern browser via
 * AnalyserNode), optional record-and-playback as a secondary check.
 * Outcome breadcrumbs flow to Logfire so we can see whether the mic
 * produces audio in the user's actual browser — independent of the call.
 *
 * Layered guard: if the volume meter never crosses the threshold the
 * user is offered an explanation + a "skip" link rather than being
 * blocked from continuing.
 */

import {
  Alert,
  Box,
  Button,
  Container,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconMicrophone,
  IconRefresh,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import { levelFromTimeDomain } from '@/services/mic-level';
import { useJourneyStep } from '@/views/journey';

type Phase =
  | 'idle'
  | 'requesting'
  | 'denied'
  | 'metering'
  | 'recording'
  | 'playback'
  | 'complete';

const RECORD_MS = 3000;
const LEVEL_PASS_THRESHOLD = 18; // 0-255 scale; ~14 is whisper, 30+ is normal speech
const LEVEL_PASS_DURATION_MS = 400; // must hold above threshold for this long

export function MicTestPage() {
  useJourneyStep(1);
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);
  const [passedThreshold, setPassedThreshold] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [canRecord, setCanRecord] = useState(true);

  // Stream + audio graph refs so we can tear down cleanly.
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const passStartRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingUrlRef = useRef<string | null>(null);
  const peakLevelRef = useRef(0);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: 5-tier media graph teardown
  const teardown = useCallback(() => {
    // Order matters: clear handlers BEFORE stopping recorder/tracks so
    // onstop can't fire after unmount and call setState on a dead
    // component. Then stop recorder, revoke any blob URL, close audio
    // graph, and finally stop the stream tracks.
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (recorderRef.current) {
      recorderRef.current.ondataavailable = null;
      recorderRef.current.onstop = null;
      if (recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch (_err) {
          // Recorder already stopped or in a transitional state — safe to ignore.
        }
      }
      recorderRef.current = null;
    }
    if (recordingUrlRef.current) {
      try {
        URL.revokeObjectURL(recordingUrlRef.current);
      } catch (_err) {
        // URL never created or already revoked — safe to ignore.
      }
      recordingUrlRef.current = null;
    }
    if (audioCtxRef.current) {
      // close() returns a Promise — silence any async rejection via
      // .catch() rather than a sync try/catch, which wouldn't see it.
      audioCtxRef.current.close().catch(() => {
        // AudioContext already closed or in error state — safe to ignore.
      });
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => {
        t.stop();
      });
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    void breadcrumb('mic_test_view');
    // Detect MediaRecorder + audio/webm support up-front.
    if (typeof MediaRecorder === 'undefined') {
      setCanRecord(false);
    }
    return () => {
      teardown();
    };
  }, [teardown]);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: getUserMedia → audio graph → metering loop
  const startMeter = useCallback(async () => {
    setErrorMsg(null);
    setLevel(0);
    setPeakLevel(0);
    peakLevelRef.current = 0;
    setPassedThreshold(false);
    passStartRef.current = null;
    setPhase('requesting');
    void breadcrumb('mic_test_start');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;

      const AudioCtx =
        window.AudioContext ||
        (window as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioCtx) {
        // Stream is open but useless without Web Audio — release it
        // immediately so the user's mic indicator doesn't stay lit.
        stream.getTracks().forEach(t => {
          t.stop();
        });
        streamRef.current = null;
        setErrorMsg(
          'Your browser does not expose the Web Audio API. Try a current version of Chrome, Edge, Firefox, or Safari.',
        );
        setPhase('denied');
        void breadcrumb('mic_test_result', {
          outcome: 'no_web_audio',
        });
        return;
      }

      const audioCtx = new AudioCtx();
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      analyserRef.current = analyser;

      setPhase('metering');

      const buf = new Uint8Array(analyser.fftSize);
      let lastSampleAt = 0;

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: per-frame meter loop + sampling + pass-threshold timing
      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        const v = levelFromTimeDomain(buf);

        setLevel(v);
        if (v > peakLevelRef.current) {
          peakLevelRef.current = v;
          setPeakLevel(v);
        }

        const now = performance.now();
        if (v >= LEVEL_PASS_THRESHOLD) {
          if (passStartRef.current === null) passStartRef.current = now;
          if (now - passStartRef.current >= LEVEL_PASS_DURATION_MS) {
            setPassedThreshold(true);
          }
        } else {
          passStartRef.current = null;
        }

        // Sample to Logfire every ~750ms so we get a trace of levels over
        // time without overwhelming the endpoint.
        if (now - lastSampleAt > 750) {
          lastSampleAt = now;
          void breadcrumb('mic_test_level_sample', {
            level: v,
            peak_level_so_far: peakLevelRef.current,
          });
        }

        rafRef.current = requestAnimationFrame(loop);
      };

      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      // Release any in-flight stream / audio graph that the post-permission
      // setup may have opened before throwing — otherwise the user's mic
      // indicator stays lit even after this catch returns.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => {
          t.stop();
        });
        streamRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {
          // AudioContext already in error state — safe to ignore.
        });
        audioCtxRef.current = null;
      }
      analyserRef.current = null;

      const name =
        err && typeof err === 'object' && 'name' in err
          ? String((err as { name: string }).name)
          : 'unknown';
      let userMsg: string;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        userMsg =
          'Microphone access was blocked. Open your browser settings, allow microphone access for this site, and reload.';
      } else if (name === 'NotFoundError') {
        userMsg =
          'No microphone detected. Plug one in or check your system sound settings, then reload.';
      } else {
        userMsg = 'Could not open the microphone. Try reloading the page.';
      }
      setErrorMsg(userMsg);
      setPhase('denied');
      void breadcrumb('mic_test_result', {
        outcome: 'getusermedia_failed',
        error_name: name,
      });
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current || !canRecord) return;
    recordedChunksRef.current = [];
    setRecordingUrl(null);
    setPhase('recording');

    try {
      const recorder = new MediaRecorder(streamRef.current);
      recorderRef.current = recorder;
      recorder.ondataavailable = e => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        // Revoke any previous URL before creating a new one — otherwise
        // multiple re-records leak blob URLs.
        if (recordingUrlRef.current) {
          try {
            URL.revokeObjectURL(recordingUrlRef.current);
          } catch (_err) {
            // Previous URL already revoked — safe to ignore.
          }
        }
        const url = URL.createObjectURL(blob);
        recordingUrlRef.current = url;
        setRecordingUrl(url);
        setPhase('playback');
      };
      recorder.start();
      setTimeout(() => {
        if (recorder.state === 'recording') {
          try {
            recorder.stop();
          } catch (_err) {
            // Recorder finalising — safe to ignore.
          }
        }
      }, RECORD_MS);
    } catch (_err) {
      // MediaRecorder unavailable in this browser — fall back to
      // "trust the meter" and keep the rest of the flow working.
      setCanRecord(false);
      setPhase('metering');
    }
  }, [canRecord]);

  const finishWithOutcome = useCallback(
    (outcome: 'pass' | 'fail') => {
      void breadcrumb('mic_test_result', {
        outcome,
        peak_level: peakLevel,
        passed_threshold: passedThreshold,
        used_playback: recordingUrl !== null,
      });
      teardown();
      setPhase('complete');
    },
    [peakLevel, passedThreshold, recordingUrl, teardown],
  );

  const skip = useCallback(() => {
    void breadcrumb('mic_test_skipped', { peak_level: peakLevel });
    teardown();
    setPhase('complete');
  }, [peakLevel, teardown]);

  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Title order={2}>Microphone check</Title>
        <Text c="dimmed">
          Before we start the interview, let's confirm your microphone is
          working. This takes about 10 seconds and helps us diagnose any audio
          issues before the call.
        </Text>

        {phase === 'idle' && (
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Group>
                <IconMicrophone size={24} />
                <Text fw={500}>Step 1: Live volume meter</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Click below to start. Your browser will ask for microphone
                permission. Then speak normally — you should see the meter
                respond.
              </Text>
              <Button
                onClick={startMeter}
                leftSection={<IconMicrophone size={16} />}
              >
                Start microphone test
              </Button>
            </Stack>
          </Paper>
        )}

        {phase === 'requesting' && (
          <Alert variant="light" title="Waiting for permission">
            Your browser is asking for microphone access. Click "Allow" to
            continue.
          </Alert>
        )}

        {phase === 'denied' && errorMsg && (
          <>
            <Alert
              variant="light"
              color="red"
              icon={<IconAlertCircle />}
              title="Microphone unavailable"
            >
              {errorMsg}
            </Alert>
            <Group>
              <Button
                variant="default"
                onClick={startMeter}
                leftSection={<IconRefresh size={16} />}
              >
                Try again
              </Button>
              <Button variant="subtle" onClick={skip}>
                Skip and continue anyway
              </Button>
            </Group>
          </>
        )}

        {(phase === 'metering' ||
          phase === 'recording' ||
          phase === 'playback') && (
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>Speak now — say "testing one two three"</Text>
                {passedThreshold && (
                  <Group gap={4}>
                    <IconCheck size={16} color="green" />
                    <Text size="sm" c="green">
                      Audio detected
                    </Text>
                  </Group>
                )}
              </Group>
              <Box>
                <Progress
                  value={Math.min(100, (level / 80) * 100)}
                  color={level >= LEVEL_PASS_THRESHOLD ? 'green' : 'gray'}
                  size="xl"
                  transitionDuration={50}
                />
                <Text size="xs" c="dimmed" mt={4}>
                  Live level: {level} · peak so far: {peakLevel}
                </Text>
              </Box>

              {canRecord && phase === 'metering' && (
                <>
                  <Text size="sm" c="dimmed">
                    Step 2 (optional but recommended): record a short sample and
                    play it back so you can hear yourself.
                  </Text>
                  <Group>
                    <Button onClick={startRecording} variant="default">
                      Record 3 seconds
                    </Button>
                    <Button
                      onClick={() =>
                        finishWithOutcome(passedThreshold ? 'pass' : 'fail')
                      }
                      color={passedThreshold ? 'green' : 'gray'}
                    >
                      {passedThreshold
                        ? 'Looks good — continue'
                        : 'Continue anyway'}
                    </Button>
                  </Group>
                </>
              )}

              {phase === 'recording' && (
                <Alert variant="light" color="blue">
                  Recording 3 seconds…
                </Alert>
              )}

              {phase === 'playback' && recordingUrl && (
                <>
                  <Text size="sm">Did you hear yourself clearly?</Text>
                  <Button
                    variant="default"
                    onClick={() => {
                      const a = new Audio(recordingUrl);
                      void a.play();
                    }}
                    aria-label="Play back the recorded microphone test"
                  >
                    ▶ Play recording
                  </Button>
                  <Group>
                    <Button
                      color="green"
                      onClick={() => finishWithOutcome('pass')}
                    >
                      Yes — continue
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => finishWithOutcome('fail')}
                    >
                      No — flag and continue
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Paper>
        )}

        {phase === 'complete' && (
          <Alert color="green" icon={<IconCheck />}>
            <Group justify="space-between">
              <Text>Microphone check complete. Result logged.</Text>
              <Button component={Link} to="/interview" size="xs">
                Continue to interview
              </Button>
            </Group>
          </Alert>
        )}
      </Stack>
    </Container>
  );
}

export default MicTestPage;
